import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { projectMembersApi } from '@/api/projectMembers';
// WEB-1866: имя действия передачи владения общее с projectsSlice.
import { TRANSFER_OWNERSHIP_ACTION_PREFIX } from './ownershipTransfer';
import type {
  Project,
  ProjectMembersState,
  ProjectMember,
  ProjectRole,
} from '@/types';
import { extractApiError } from '@/api/errorStatus';
import { rejectedApiError } from './rejectedApiError';

const initialState: ProjectMembersState = {
  members: [],
  membersProjectId: null,
  myRole: null,
  myCapabilities: [],
  myCapabilitiesProjectId: null,
  myRoleRequestedProjectId: null,
  myRoleRequestId: null,
  isLoading: false,
  error: null,
  membersLoadFailed: false,
};

/**
 * WEB-4089: ответ `my-role` поздний — пришёл не на последний запрос
 * (`myRoleRequestId`, номер запроса thunk). Сверка по номеру запроса, а не по
 * проекту (возврат 1): при быстром A → B → A отказ ПЕРВОГО запроса по A иначе
 * сбросил бы права второго. Пустая метка — запроса через `pending` не было
 * (засев прав в тестовых хранилищах, окно после центрального сброса): ответ
 * применяется, как раньше.
 */
function isLateMyRoleAnswer(
  state: ProjectMembersState,
  requestId: string,
): boolean {
  const latest = state.myRoleRequestId ?? null;
  return latest !== null && latest !== requestId;
}

export const fetchMyRoleThunk = createAsyncThunk(
  'projectMembers/fetchMyRole',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await projectMembersApi.getMyRole(projectId);
      const data = response.data as {
        role: ProjectRole;
        capabilities?: unknown;
      };
      // Validate at the boundary: only accept a string[] of capabilities.
      const capabilities = Array.isArray(data.capabilities)
        ? data.capabilities.filter((c): c is string => typeof c === 'string')
        : [];
      // WEB-1343 п.1: carry the projectId this response answers for, so a
      // consumer (useProjectRole) can refuse to apply it to a DIFFERENT
      // active project — see ProjectMembersState.myCapabilitiesProjectId.
      return { role: data.role, capabilities, projectId };
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

export const fetchMembersThunk = createAsyncThunk(
  'projectMembers/fetchAll',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await projectMembersApi.list(projectId);
      const data = response.data as { members: ProjectMember[]; total: number };
      return data.members;
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

export const updateRoleThunk = createAsyncThunk(
  'projectMembers/updateRole',
  async (
    {
      projectId,
      userId,
      role,
    }: { projectId: string; userId: string; role: ProjectRole },
    { rejectWithValue },
  ) => {
    try {
      const response = await projectMembersApi.updateRole(projectId, userId, {
        role,
      });
      return response.data as ProjectMember;
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

export const removeMemberThunk = createAsyncThunk(
  'projectMembers/remove',
  async (
    { projectId, userId }: { projectId: string; userId: string },
    { rejectWithValue },
  ) => {
    try {
      await projectMembersApi.remove(projectId, userId);
      return userId;
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

// project-settings-mockup item 18: self-leave — calls DELETE
// /projects/:id/members/me (WEB-205 §F9), distinct from removeMemberThunk's
// DELETE /members/:userId. userId is the acting user's own id — carried so
// the reducer can filter them out of `members` the same way removeMemberThunk
// does, without needing a separate currentUserId lookup in the slice.
export const leaveProjectThunk = createAsyncThunk(
  'projectMembers/leave',
  async (
    { projectId, userId }: { projectId: string; userId: string },
    { rejectWithValue },
  ) => {
    try {
      await projectMembersApi.leave(projectId);
      return userId;
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

/**
 * WEB-1866 — потеряло ли ДЕЙСТВУЮЩЕЕ ЛИЦО владение этим проектом успешной
 * передачей.
 *
 * Зачем считать здесь, а не в редьюсерах: ответ сервера — строка НОВОГО
 * владельца, кто нажал кнопку в ней не написано, а срезы `auth`/`projects`/
 * `organizations` редьюсеру среза участников недоступны. В создателе запроса
 * доступно всё состояние разом, и обе двери передачи (вкладка «Участники» в
 * настройках проекта и карточка команды организации) ходят через ЭТОТ thunk —
 * значит признак считается один раз для обеих, а не копией на каждом экране.
 *
 * Правда «я был владельцем» берётся из СТРОКИ УЧАСТНИКА, а не из моей роли:
 * роль на ручке my-role и в списке проектов — ЭФФЕКТИВНАЯ (максимум роли в
 * проекте и роли в организации, backend effective-project-role.util.ts), и
 * владелец организации видит «владелец» на каждом проекте организации, хотя
 * своей строки владельца там не имеет. Передача двигает ровно строки
 * участников: если владельческая строка была не моя, моя роль не изменилась
 * вовсе.
 *
 * И обратная сторона того же правила: если я владелец ОРГАНИЗАЦИИ, то после
 * потери владельческой строки сервер по-прежнему считает меня владельцем
 * проекта (эффективная роль). Понижать себя в кабинете в этом случае — то же
 * враньё, только в другую сторону (кнопки исчезли бы у того, кому сервер их
 * разрешает), поэтому такой случай исключён.
 *
 * Неизвестность (список участников не загружен, пользователь не известен) —
 * «не понижаем»: это ровно сегодняшнее поведение экрана, а не новое.
 *
 * Ревизия пачки 6 добавила сюда две неизвестности, которых предикат раньше
 * не различал и потому отвечал наугад:
 *
 * 1) СОСТАВ КОМАНДЫ ОТ ДРУГОГО ПРОЕКТА. `members` — одна глобальная ячейка на
 *    весь кабинет, и её пишет любой экран (в том числе карточка «Команда
 *    организации», листающая произвольные проекты). Без метки проекта строка
 *    владельца из ЧУЖОГО состава отвечала на вопрос «был ли я владельцем
 *    ЭТОГО проекта». Метка `membersProjectId` — тот же приём, что у бандла
 *    прав (`myCapabilitiesProjectId`, WEB-1343): метка ≠ projectId →
 *    неизвестность → «не понижаем».
 *
 * 2) ОРГАНИЗАЦИЯ ПРОЕКТА НЕИЗВЕСТНА. Исключение «владелец организации себя не
 *    понижает» искало проект только в срезе проектов, а вторая дверь передачи
 *    (ProjectTeamCard) держит список проектов организации в своём локальном
 *    состоянии и умеет смотреть НЕ активную организацию — проекта в срезе
 *    может не быть вовсе, и орг-роль вычислялась как null, то есть владелец
 *    организации понижал себя (тот же класс, что WEB-1417: орг-роль сверяется
 *    по организации ПРОЕКТА). Лечится с двух концов: основной путь — дверь
 *    передаёт `orgId` (карточка знает его пропом), запас — проекта нет и
 *    orgId не назван → неизвестность → «не понижаем».
 */
interface TransferOwnershipStateView {
  auth?: { user?: { id?: string | null } | null } | null;
  projectMembers?: {
    members?: ProjectMember[];
    membersProjectId?: string | null;
  } | null;
  projects?: {
    projects?: Project[];
    currentProject?: Project | null;
  } | null;
  organizations?: { orgs?: { id: string; role?: string | null }[] } | null;
}

function didActorLoseOwnership(
  state: TransferOwnershipStateView,
  projectId: string,
  orgId?: string | null,
): boolean {
  const actorUserId = state.auth?.user?.id ?? null;
  if (!actorUserId) return false;

  // (1) Состав команды обязан быть ИМЕННО этого проекта — иначе строка
  // владельца ничего не доказывает про него.
  const membersProjectId = state.projectMembers?.membersProjectId ?? null;
  if (membersProjectId !== projectId) return false;

  const previousOwnerId =
    state.projectMembers?.members?.find((m) => m.role === 'owner')?.userId ??
    null;
  if (previousOwnerId !== actorUserId) return false;

  const project =
    state.projects?.projects?.find((p) => p.id === projectId) ??
    (state.projects?.currentProject?.id === projectId
      ? state.projects.currentProject
      : null);
  // (2) Организация проекта: сперва то, что назвала дверь, затем сам проект
  // из среза. Ни того, ни другого — организация неизвестна, роль не трогаем.
  const resolvedOrgId = orgId ?? project?.orgId ?? null;
  if (!resolvedOrgId) {
    // Проект в срезе есть, но у объекта нет orgId — это старый объект,
    // загруженный до WEB-1417; прежнее поведение для него сохраняем.
    return project !== null;
  }
  const orgRole =
    state.organizations?.orgs?.find((o) => o.id === resolvedOrgId)?.role ?? null;
  // Владелец организации остаётся владельцем проекта и без строки участника.
  if (orgRole === 'owner') return false;

  return true;
}

/**
 * WEB-1559 — передача владения проектом.
 *
 * Отдельный thunk, а не `updateRoleThunk` с ролью 'owner': сервер такую роль
 * через смену роли по-прежнему не принимает (400 «Cannot assign owner role»),
 * и это осознанно — передача владения двигает ДВЕ строки, а смена роли одну.
 */
export const transferOwnershipThunk = createAsyncThunk(
  TRANSFER_OWNERSHIP_ACTION_PREFIX,
  async (
    {
      projectId,
      newOwnerId,
      orgId,
    }: {
      projectId: string;
      newOwnerId: string;
      /**
       * WEB-1866 (ревизия пачки 6): организация проекта — её называет ДВЕРЬ,
       * если знает (карточка «Команда организации» знает пропом, и проекта в
       * срезе проектов при этом может не быть). Нужна ровно для исключения
       * «владелец организации себя не понижает» — см. didActorLoseOwnership.
       */
      orgId?: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const response = await projectMembersApi.transferOwnership(projectId, {
        newOwnerId,
      });
      const member = response.data as ProjectMember;
      return {
        member,
        projectId,
        // WEB-1866: см. actorLostOwnership ниже.
        actorLostOwnership: didActorLoseOwnership(
          getState() as TransferOwnershipStateView,
          projectId,
          orgId,
        ),
      };
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

const projectMembersSlice = createSlice({
  name: 'projectMembers',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    // WEB-1343 п.2/9: explicit full reset of team/role/capabilities — used
    // (a) centrally on active-project/org switch (store/index.ts) so stale
    // buttons don't linger between the switch and the next refetch, and
    // (b) by ProjectTeamCard when the VIEWED org changes, so the previous
    // org's team/role don't stay on screen while the new org's projects load.
    resetProjectMembers() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // fetchMyRole
    builder
      // WEB-4089: права грузит оболочка по каждой смене проекта; при быстром
      // переключении A → B ответ по A может прийти после ответа по B.
      // `pending` запоминает проект и номер последнего запроса, а `fulfilled`
      // и `rejected` применяются, только когда номер пуст (засев в тестах без
      // `pending`) или равен их номеру, — иначе ответ поздний и ничего не
      // меняет. По ответу метки не снимаются; снимает их сброс среза — и тогда
      // оболочка спрашивает права заново (useShellProjectRights).
      .addCase(fetchMyRoleThunk.pending, (state, action) => {
        state.myRoleRequestedProjectId = action.meta.arg;
        state.myRoleRequestId = action.meta.requestId;
      })
      .addCase(fetchMyRoleThunk.fulfilled, (state, action) => {
        if (isLateMyRoleAnswer(state, action.meta.requestId)) return;
        state.myRole = action.payload.role;
        state.myCapabilities = action.payload.capabilities;
        state.myCapabilitiesProjectId = action.payload.projectId;
      })
      .addCase(fetchMyRoleThunk.rejected, (state, action) => {
        if (isLateMyRoleAnswer(state, action.meta.requestId)) return;
        state.error = rejectedApiError(action);
        // WEB-1366 хвост (разбор 16.08, п.2, «Команда проекта»): без сброса
        // здесь myRole/myCapabilities остаются от ПРЕДЫДУЩЕГО проекта после
        // переключения — карточка показывала бы человеку чужие права.
        state.myRole = null;
        state.myCapabilities = [];
        state.myCapabilitiesProjectId = null;
      });

    // fetchAll
    builder
      .addCase(fetchMembersThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.membersLoadFailed = false;
      })
      .addCase(fetchMembersThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.membersLoadFailed = false;
        state.members = action.payload;
        // WEB-1866 (ревизия пачки 6): чей это состав. Берём из аргумента
        // запроса (meta.arg), а не из ответа — сервер отдаёт только массив
        // участников, projectId в нём не написан. Единственное место, где
        // состав ЗАМЕНЯЕТСЯ целиком; остальные редьюсеры (смена роли,
        // удаление, выход, передача владения) правят уже помеченный список
        // того же проекта, а resetProjectMembers снимает метку вместе с ним.
        // meta может отсутствовать у РУКОТВОРНОГО действия того же имени
        // (тестовые посевы) — тогда честный ответ «чей состав» неизвестен.
        state.membersProjectId = action.meta?.arg ?? null;
      })
      .addCase(fetchMembersThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = rejectedApiError(action);
        state.membersLoadFailed = true;
      });

    // updateRole
    builder.addCase(updateRoleThunk.fulfilled, (state, action) => {
      state.members = state.members.map((m) =>
        m.userId === action.payload.userId ? action.payload : m,
      );
    });

    // remove
    builder.addCase(removeMemberThunk.fulfilled, (state, action) => {
      state.members = state.members.filter((m) => m.userId !== action.payload);
    });

    // leave (self)
    builder.addCase(leaveProjectThunk.fulfilled, (state, action) => {
      state.members = state.members.filter((m) => m.userId !== action.payload);
    });

    // WEB-1559: передача владения. Сервер отвечает строкой НОВОГО владельца,
    // но за один запрос изменились ДВЕ строки: прежний владелец стал
    // администратором в той же транзакции. Список должен показать оба
    // изменения сразу, иначе в таблице на мгновение окажется два владельца —
    // состояние, которого в базе физически не бывает (частичный уникальный
    // индекс WEB-1554). Отражаем ровно то, что сделал сервер.
    builder.addCase(transferOwnershipThunk.fulfilled, (state, action) => {
      const newOwner = action.payload.member;
      state.members = state.members.map((m) => {
        if (m.userId === newOwner.userId) return newOwner;
        if (m.role === 'owner') return { ...m, role: 'admin' as ProjectRole };
        return m;
      });

      // WEB-1866: моя роль изменилась ЭТИМ ЖЕ ответом — владелец стал
      // администратором. Ставим её сразу, не дожидаясь перечитывания my-role:
      // до правки экран между ответом передачи и ответом my-role показывал
      // владельческие кнопки, а если my-role отказывал (моргнула сеть), то и
      // после — его отказ обнуляет бандл, и роль откатывается на устаревшую
      // «владелец» из списка проектов (вторая половина фикса — projectsSlice).
      if (action.payload.actorLostOwnership) {
        state.myRole = 'admin';
        // Набор прав в бандле — ВЛАДЕЛЬЧЕСКИЙ, он этим же ответом протух.
        // Роль мы знаем, права — нет, поэтому бандл снимаем: потребители
        // (useProjectRole) откатятся на роль, а не на устаревшие права.
        state.myCapabilities = [];
        state.myCapabilitiesProjectId = null;
      }
    });
  },
});

export const { clearError, resetProjectMembers } = projectMembersSlice.actions;
export default projectMembersSlice.reducer;
